// bill_subsystem_v1 + bill_dashboard_v1: dashboard with summary cards, top customers, monthly trend
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../api';
import CustomerInvoicesModal from './CustomerInvoicesModal'; /* by_month_v1 */

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtMonth(s) {
  if (!s) return '-';
  var parts = s.split('-');
  var monthNames = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  if (parts.length !== 2) return s;
  var year = Number(parts[0]) + 543;
  return monthNames[Number(parts[1]) - 1] + ' ' + year;
}

function StatCard(props) {
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{props.label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: props.color || '#1e293b' }}>{props.value}</div>
      {props.sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{props.sub}</div>}
    </div>
  );
}

function StatusPill(props) {
  var colors = {
    draft: { bg: '#f1f5f9', fg: '#475569', label: 'ร่าง' },
    sent: { bg: '#dbeafe', fg: '#1d4ed8', label: 'ส่งแล้ว' },
    viewed: { bg: '#e0e7ff', fg: '#4338ca', label: 'ลูกค้าดู' },
    paid: { bg: '#dcfce7', fg: '#15803d', label: 'จ่ายแล้ว' },
    overdue: { bg: '#fed7aa', fg: '#c2410c', label: 'เกินกำหนด' },
    cancelled: { bg: '#fee2e2', fg: '#b91c1c', label: 'ยกเลิก' },
  };
  var c = colors[props.status] || { bg: '#f1f5f9', fg: '#475569', label: props.status };
  return (
    <div style={{ background: c.bg, color: c.fg, padding: '8px 12px', borderRadius: 10, textAlign: 'center', minWidth: 90 }}>
      <div style={{ fontSize: 11, fontWeight: 500 }}>{c.label}</div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{props.count}</div>
    </div>
  );
}

export default function BillDashboard() {
  const ctx = useOutletContext();
  const user = ctx && ctx.user;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /* by_month_v1: state for monthly customer breakdown + modal */
  const [selectedMonth, setSelectedMonth] = useState(function () { var d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'); });
  const [byMonthData, setByMonthData] = useState({ customers: [], month: '' });
  const [byMonthLoading, setByMonthLoading] = useState(false);
  const [modalCustomer, setModalCustomer] = useState(null);
  /* dashboard_duedate_filter_v1: filter by due_date */
  const [dashMonth, setDashMonth] = useState(function () { var d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'); });
  const [dashFrom, setDashFrom] = useState('');
  const [dashTo, setDashTo] = useState('');

  /* by_month_v1: load customers for selected month */
  useEffect(function () {
    if (!selectedMonth) return;
    setByMonthLoading(true);
    API.get('/api/bill/dashboard/by-month?month=' + selectedMonth)
      .then(function (r) { setByMonthData(r.data || { customers: [], month: selectedMonth }); })
      .catch(function () {})
      .finally(function () { setByMonthLoading(false); });
  }, [selectedMonth]);

  /* dashboard_duedate_filter_v1: month/range -> from,to */
  var dashRangeActive = !!(dashFrom || dashTo);
  var dashF = dashRangeActive ? dashFrom : (dashMonth ? dashMonth + '-01' : '');
  var dashT = dashRangeActive ? dashTo : (dashMonth ? (function () { var p = dashMonth.split('-'); var y = Number(p[0]); var m = Number(p[1]); var last = new Date(y, m, 0).getDate(); return dashMonth + '-' + String(last).padStart(2, '0'); })() : '');
  useEffect(function () {
    setLoading(true);
    var qs = [];
    if (dashF) qs.push('from=' + dashF);
    if (dashT) qs.push('to=' + dashT);
    var url = '/api/bill/dashboard/stats' + (qs.length ? ('?' + qs.join('&')) : '');
    API.get(url)
      .then(function (r) { setData(r.data); })
      .catch(function (e) { setError('โหลด stats ไม่สำเร็จ'); })
      .finally(function () { setLoading(false); });
  }, [dashF, dashT]);

  if (loading) return <div style={{ padding: 24 }}>กำลังโหลด...</div>;
  if (error) return <div style={{ padding: 24, color: '#dc2626' }}>⚠️ {error}</div>;
  if (!data) return null;

  var summary = data.summary || {};
  var statusBreakdown = summary.status_breakdown || {};
  var topCustomers = data.top_customers || [];
  var trend = data.monthly_trend || [];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>📊 Dashboard</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
          สวัสดี {user && (user.name || user.email)} — สรุปข้อมูลบิลของบริษัท
        </p>
      </div>

      {/* dashboard_duedate_filter_v1: filter bar (due_date) */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: '#64748b' }}>ครบกำหนด:</span>
        <input type="month" value={dashRangeActive ? '' : dashMonth} onChange={function (e) { setDashFrom(''); setDashTo(''); setDashMonth(e.target.value); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }} />
        <span style={{ color: '#94a3b8', fontSize: 12 }}>หรือช่วง</span>
        <input type="date" value={dashFrom} onChange={function (e) { setDashFrom(e.target.value); }} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }} />
        <span style={{ color: '#94a3b8', fontSize: 12 }}>-</span>
        <input type="date" value={dashTo} onChange={function (e) { setDashTo(e.target.value); }} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }} />
        {dashRangeActive ? <button onClick={function () { setDashFrom(''); setDashTo(''); }} style={{ background: 'none', border: '1px solid #e2e8f0', padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#64748b' }}>ล้างช่วง</button> : null}
      </div>
      {/* ===== SUMMARY CARDS ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="📄 จำนวนบิลทั้งหมด" value={summary.total_invoices} sub="ตั้งแต่เริ่มใช้งาน" />
        <StatCard label="⏳ ยอดรอเก็บ" value={'฿' + fmtMoney(summary.outstanding_amount)} color="#c2410c" sub="sent + viewed + overdue" />
        <StatCard label="✅ ยอดเก็บแล้ว" value={'฿' + fmtMoney(summary.paid_amount)} color="#15803d" sub="ทั้งหมด" />
        <StatCard label={dashRangeActive || dashMonth ? "📅 ในช่วงที่เลือก" : "📅 เดือนนี้"} value={'฿' + fmtMoney(summary.paid_this_month)} color="#4338ca" sub={dashRangeActive || dashMonth ? "จ่ายในช่วงที่เลือก" : "ที่จ่ายในเดือนนี้"} />
        {/* bill_dashboard_usdt_v1: ยอด USDT รอเก็บ/เก็บแล้ว (คู่เหมือนหน้าใบแจ้งหนี้) */}
        <StatCard label="⏳ ยอดรอเก็บ USDT" value={fmtMoney(summary.outstanding_usdt) + ' USDT'} color="#c2410c" sub="sent + viewed + overdue" />
        <StatCard label="✅ ยอดเก็บแล้ว USDT" value={fmtMoney(summary.paid_usdt) + ' USDT'} color="#15803d" sub="ทั้งหมด" />
      </div>

      {/* ===== STATUS BREAKDOWN ===== */}
      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>📋 แยกตามสถานะ</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <StatusPill status="draft" count={statusBreakdown.draft} />
          <StatusPill status="sent" count={statusBreakdown.sent} />
          <StatusPill status="viewed" count={statusBreakdown.viewed} />
          <StatusPill status="paid" count={statusBreakdown.paid} />
          <StatusPill status="overdue" count={statusBreakdown.overdue} />
          <StatusPill status="cancelled" count={statusBreakdown.cancelled} />
        </div>
      </div>

      {/* ===== TOP CUSTOMERS ===== */}
      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>🏆 Top 5 ลูกค้า (ตามยอดรวม)</h3>
        {topCustomers.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 20 }}>ยังไม่มีข้อมูล</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>อันดับ</th>
                <th style={{ padding: 8, textAlign: 'left' }}>ลูกค้า</th>
                <th style={{ padding: 8, textAlign: 'center' }}>จำนวนบิล</th>
                <th style={{ padding: 8, textAlign: 'right' }}>ยอดรวม</th>
                <th style={{ padding: 8, textAlign: 'right' }}>เก็บแล้ว</th>
                <th style={{ padding: 8, textAlign: 'right' }}>รอเก็บ</th>
              </tr>
            </thead>
            <tbody>
              {topCustomers.map(function (c, idx) {
                var medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : (idx + 1) + '.';
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 8, fontWeight: 600 }}>{medal}</td>
                    <td style={{ padding: 8 }}>{c.name}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>{c.invoice_count}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>฿{fmtMoney(c.total_amount)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#15803d' }}>฿{fmtMoney(c.paid_amount)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#c2410c' }}>฿{fmtMoney(c.outstanding_amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== BY-MONTH CUSTOMERS ===== by_month_v1 */}
      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>👥 ลูกค้าในเดือนที่เลือก</h3>
          <input type="month" value={selectedMonth} onChange={function (e) { setSelectedMonth(e.target.value); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }} />
        </div>
        {byMonthLoading && <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>กำลังโหลด...</div>}
        {!byMonthLoading && byMonthData.customers.length === 0 && (
          <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13 }}>ไม่มีลูกค้าในเดือนนี้</div>
        )}
        {!byMonthLoading && byMonthData.customers.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>ลูกค้า</th>
                <th style={{ padding: 8, textAlign: 'center' }}>ช่อง</th>
                <th style={{ padding: 8, textAlign: 'center' }}>บิล</th>
                <th style={{ padding: 8, textAlign: 'right' }}>รวม</th>
                <th style={{ padding: 8, textAlign: 'right' }}>เก็บแล้ว</th>
                <th style={{ padding: 8, textAlign: 'right' }}>รอเก็บ</th>
                <th style={{ padding: 8, textAlign: 'center' }}>ดู</th>
              </tr>
            </thead>
            <tbody>
              {byMonthData.customers.map(function (c) {
                return (
                  <tr key={c.customer_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 8, fontWeight: 600 }}>
                      <button onClick={function () { setModalCustomer({ id: c.customer_id, name: c.customer_name }); }} style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 13, fontFamily: 'inherit' }}>{c.customer_name}</button>
                    </td>
                    <td style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>
                      {c.has_telegram && <span title="Telegram">📱</span>}
                      {c.has_line && <span title="LINE">💬</span>}
                      {!c.has_telegram && !c.has_line && <span style={{ color: '#cbd5e1' }}>-</span>}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>{c.invoice_count}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>฿{fmtMoney(c.total_amount)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#15803d' }}>฿{fmtMoney(c.paid_amount)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#c2410c' }}>฿{fmtMoney(c.outstanding_amount)}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <button onClick={function () { setModalCustomer({ id: c.customer_id, name: c.customer_name }); }} style={{ background: '#dbeafe', border: '1px solid #93c5fd', color: '#1d4ed8', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>👁️</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== MONTHLY TREND ===== */}
      <div style={{ background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>📈 รายเดือน (6 เดือนล่าสุด)</h3>
        {trend.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 20 }}>ยังไม่มีข้อมูล</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>เดือน</th>
                <th style={{ padding: 8, textAlign: 'center' }}>จำนวนบิล</th>
                <th style={{ padding: 8, textAlign: 'right' }}>ยอดรวม</th>
                <th style={{ padding: 8, textAlign: 'right' }}>เก็บแล้ว</th>
                <th style={{ padding: 8, textAlign: 'right' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {trend.map(function (m) {
                var pct = m.total > 0 ? Math.round((m.paid / m.total) * 100) : 0;
                return (
                  <tr key={m.month} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 8, fontWeight: 600 }}>{fmtMonth(m.month)}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>{m.count}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>฿{fmtMoney(m.total)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#15803d' }}>฿{fmtMoney(m.paid)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: pct >= 80 ? '#15803d' : pct >= 50 ? '#c2410c' : '#dc2626' }}>{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* by_month_v1: customer invoices modal */}
      {modalCustomer && <CustomerInvoicesModal customerId={modalCustomer.id} customerName={modalCustomer.name} month={selectedMonth} onClose={function () { setModalCustomer(null); }} />}
    </div>
  );
}
